import { Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ADMIN_MENU_ICON_BY_ROUTE, type AdminMenuIconName } from './admin-icons';
import { AdminIconComponent } from './admin-icon.component';
import { AdminSidebarItemComponent } from './admin-sidebar-item.component';

export type AdminMenuChild = {
  label: string;
  route: string;
  roles?: string[];
};

@Component({
  selector: 'app-admin-sidebar-group',
  standalone: true,
  imports: [AdminIconComponent, AdminSidebarItemComponent],
  template: `
    <div
      class="cg-admin-sidebar-group"
      [class.is-expanded]="expanded()"
      [class.has-active-child]="hasActiveChild()"
    >
      <button
        type="button"
        class="cg-admin-sidebar-item cg-admin-sidebar-group__toggle"
        [class.is-active]="hasActiveChild()"
        [attr.aria-expanded]="expanded()"
        (click)="onToggle()"
      >
        <span class="cg-admin-sidebar-item__icon" aria-hidden="true">
          <app-admin-icon [name]="icon()" />
        </span>
        <span class="cg-admin-sidebar-item__label">{{ label() }}</span>
        @if (!collapsed()) {
          <span class="cg-admin-sidebar-group__chevron" aria-hidden="true">
            <app-admin-icon name="chevron-right" [size]="16" />
          </span>
        }
      </button>
      @if (!collapsed()) {
        <div
          class="cg-admin-sidebar-group__children-wrap"
          [class.is-open]="expanded()"
          [attr.aria-hidden]="!expanded()"
        >
          <div class="cg-admin-sidebar-group__children" role="group" [attr.aria-label]="label()">
          @for (child of children(); track child.route) {
            <div class="cg-admin-sidebar-group__child">
              <app-admin-sidebar-item
                [label]="child.label"
                [route]="child.route"
                [icon]="iconFor(child.route)"
                (click)="navSelected.emit()"
              />
            </div>
          }
          </div>
        </div>
      }
    </div>
  `,
})
export class AdminSidebarGroupComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly label = input.required<string>();
  readonly icon = input.required<AdminMenuIconName>();
  readonly children = input.required<AdminMenuChild[]>();
  readonly defaultExpanded = input(true);
  readonly collapsed = input(false);
  readonly navSelected = output<void>();

  readonly expanded = signal(false);
  private readonly activeUrl = signal(this.router.url);

  readonly hasActiveChild = computed(() =>
    this.children().some((child) => this.isRouteActive(this.activeUrl(), child.route)),
  );

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.activeUrl.set(e.urlAfterRedirects);
      });
  }

  ngOnInit(): void {
    this.expanded.set(this.defaultExpanded());
  }

  onToggle(): void {
    this.expanded.update((v) => !v);
  }

  iconFor(route: string): AdminMenuIconName {
    return ADMIN_MENU_ICON_BY_ROUTE[route] ?? 'layout-dashboard';
  }

  private isRouteActive(url: string, route: string): boolean {
    return url === route || url.startsWith(route + '/');
  }
}
